import { useCallback, useEffect, useState } from "react";
import { WifiOff, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { flushQueue, isOffline, pendingCount } from "@/lib/offline";
import { flushPhotos, offlinePhotoCount } from "@/lib/offlinePhotos";

export const OfflineBanner = ({ onSynced }) => {
  const [offline, setOffline] = useState(isOffline());
  const [pending, setPending] = useState(pendingCount());
  const refresh = useCallback(async () => setPending(pendingCount() + (await offlinePhotoCount())), []);
  const [syncing, setSyncing] = useState(false);

  const sync = useCallback(async (silent = false) => {
    if (isOffline()) return;
    const photos = await offlinePhotoCount();
    if (!pendingCount() && !photos) return;
    setSyncing(true);
    const queued = await flushQueue();
    const uploaded = await flushPhotos();
    const synced = queued.synced + uploaded.synced;
    const failed = queued.failed + uploaded.failed;
    await refresh();
    setSyncing(false);
    if (synced && !silent) toast.success(`${synced} élément(s) synchronisé(s).`);
    if (failed) toast.error(`${failed} élément(s) n'ont pas pu être envoyés — nouvel essai plus tard.`);
    if (synced) onSynced?.();
  }, [onSynced, refresh]);

  useEffect(() => {
    const goOnline = () => { setOffline(false); sync(); };
    const goOffline = () => { setOffline(true); refresh(); };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const timer = setInterval(refresh, 4000);
    refresh();
    if (!isOffline()) sync(true);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      clearInterval(timer);
    };
  }, [sync, refresh]);

  if (!offline && !pending) return null;

  return (
    <div
      data-testid="offline-banner"
      className={`mt-5 flex flex-wrap items-center gap-3 rounded-2xl border px-4 py-3 ${offline ? "border-amber-200 bg-amber-50" : "border-[#C9D4F4] bg-[#EEF1FC]"}`}
    >
      {offline ? <WifiOff className="w-4 h-4 text-amber-700" /> : <CheckCircle2 className="w-4 h-4 text-[#3A5CC8]" />}
      <p className="text-sm font-medium text-[#0F2645]">
        {offline
          ? "Mode chantier hors ligne — notes, mesures, matériel et photos sont conservés sur l'appareil."
          : "Éléments enregistrés hors ligne, prêts à être synchronisés."}
      </p>
      {!!pending && <span data-testid="offline-pending" className="rounded-full bg-white border border-[#E2E7F7] px-2.5 py-0.5 text-xs font-bold text-[#0F2645]">{pending} en attente</span>}
      {!offline && !!pending && (
        <button type="button" data-testid="offline-sync-btn" disabled={syncing} onClick={() => sync()} className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#3A5CC8] px-4 py-2 text-xs font-semibold text-white hover:bg-[#2F4CA8] disabled:opacity-40 transition-colors">
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> Synchroniser
        </button>
      )}
    </div>
  );
};
